import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Bot, Sparkles, Copy, RefreshCw, Sliders, MessageSquare, Terminal, Lightbulb } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

export const PromptEngineering = () => {
    const { completeModule } = useAcademyStore();
    const [role, setRole] = useState('');
    const [task, setTask] = useState('');
    const [context, setContext] = useState('');
    const [format, setFormat] = useState('');
    const [temperature, setTemperature] = useState(0.7);
    const [generatedResponse, setGeneratedResponse] = useState<string | null>(null);
    const [isThinking, setIsThinking] = useState(false);
    const [completed, setCompleted] = useState(false);

    const fullPrompt = `${role ? `ACT PROMPT:\nRole: ${role}\n` : ''}${context ? `Context: ${context}\n` : ''}Task: ${task}\n${format ? `Output Format: ${format}` : ''}`;

    const handleGenerate = () => {
        setIsThinking(true);
        setGeneratedResponse(null);

        // Quality Scoring Logic
        let score = 0;
        if (role.length > 5) score += 20;
        if (task.length > 10) score += 40;
        if (context.length > 10) score += 20;
        if (format.length > 2) score += 20;

        // Simulation Delay
        setTimeout(() => {
            setIsThinking(false);

            // Educational Feedback Mock
            let response = "";
            let feedback = "";

            if (score < 40) {
                response = "I'm not sure what you want. Can you be more specific?";
                feedback = "⚠️ Too Vague! Try adding a Persona and specific Constraints.";
            } else if (score < 80) {
                response = `Sure, I can help with that.\nHere is the information about ${task} you requested.`;
                feedback = "👍 Good start! Add Context (Why?) and Format (How?) to make it amazing.";
            } else {
                response = `🤖 [${role.toUpperCase()} MODE ONLINE]\n\nBased on the context provided ("${context.substring(0, 20)}..."), here is the optimal solution:\n\n1. 🔍 Analysis: Discovered key patterns.\n2. 🛠️ Execution: Implemented strategy.\n3. ✅ Result: Success confirmed.\n\nFormatted as requested in: ${format}`;
                feedback = "🚀 ACCESS GRANTED! Perfect 4-Part Prompt Structure.";

                if (!completed) {
                    setCompleted(true);
                    completeModule('prompt-engineering', 150);
                }
            }

            setGeneratedResponse(response);
        }, 1500);
    };

    return (
        <div className="min-h-screen bg-[#f0f9ff] flex flex-col font-sans">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-8 py-4 flex justify-between items-center sticky top-0 z-30 shadow-sm">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-purple-600 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="hidden md:flex bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold items-center gap-1"><Sparkles size={12} /> Module Complete</div>}
                    <div className="bg-purple-100 text-purple-800 px-4 py-1 rounded-full text-sm font-bold border border-purple-200 flex items-center gap-2">
                        <Bot size={16} /> The Prompt Lab
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">

                {/* LEFT: ENGINEERING CONSOLE */}
                <div className="w-full lg:w-1/2 p-8 overflow-y-auto bg-slate-50">
                    <div className="max-w-xl mx-auto space-y-8">
                        <div>
                            <h1 className="text-3xl font-black text-slate-800 mb-2 font-comic">Prompt Engineering 🧪</h1>
                            <p className="text-slate-600 text-sm">Design the perfect instruction set. A great prompt has 4 parts: <strong>Role, Task, Context, Format</strong>.</p>
                        </div>

                        {/* Control Panel */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 space-y-6">

                            {/* 1. ROLE */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                    <div className="w-6 h-6 rounded bg-purple-100 text-purple-600 flex items-center justify-center text-xs">1</div>
                                    Role / Persona <span className="text-slate-400 font-normal">(Who is the AI?)</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {['Senior Python Dev', 'Marketing Guru', 'Data Scientist', 'Socratic Tutor'].map(r => (
                                        <button
                                            key={r}
                                            onClick={() => setRole(r)}
                                            className={`px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${role === r ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-slate-200 text-slate-600 hover:border-purple-300'}`}
                                        >
                                            {r}
                                        </button>
                                    ))}
                                    <input
                                        type="text"
                                        placeholder="Or type custom role..."
                                        value={role}
                                        onChange={e => setRole(e.target.value)}
                                        className="col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-purple-400 outline-none"
                                    />
                                </div>
                            </div>

                            {/* 2. TASK */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                    <div className="w-6 h-6 rounded bg-blue-100 text-blue-600 flex items-center justify-center text-xs">2</div>
                                    Task <span className="text-slate-400 font-normal">(What to do?)</span>
                                </label>
                                <textarea
                                    className="w-full h-20 p-3 rounded-xl border border-slate-200 text-sm focus:border-blue-400 outline-none resize-none"
                                    placeholder="Explain the Quantum Theory of Gravity..."
                                    value={task}
                                    onChange={e => setTask(e.target.value)}
                                />
                            </div>

                            {/* 3. CONTEXT */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                    <div className="w-6 h-6 rounded bg-green-100 text-green-600 flex items-center justify-center text-xs">3</div>
                                    Context <span className="text-slate-400 font-normal">(Constraints & Background)</span>
                                </label>
                                <textarea
                                    className="w-full h-20 p-3 rounded-xl border border-slate-200 text-sm focus:border-green-400 outline-none resize-none"
                                    placeholder="Audience is 5 year olds. Use emojis. Keep it under 280 characters."
                                    value={context}
                                    onChange={e => setContext(e.target.value)}
                                />
                            </div>

                            {/* 4. FORMAT */}
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                    <div className="w-6 h-6 rounded bg-orange-100 text-orange-600 flex items-center justify-center text-xs">4</div>
                                    Output Format <span className="text-slate-400 font-normal">(How it looks)</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {['Bullet Points', 'Markdown Table', 'JSON', 'Python Code', 'Haiku'].map(f => (
                                        <button
                                            key={f}
                                            onClick={() => setFormat(f)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${format === f ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 text-slate-500 hover:border-orange-300'}`}
                                        >
                                            {f}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Temperature Slider */}
                            <div className="pt-4 border-t border-slate-100">
                                <div className="flex justify-between text-xs font-bold text-slate-500 mb-2">
                                    <span>Precise (0.0)</span>
                                    <span>Temperature: {temperature}</span>
                                    <span>Creative (1.0)</span>
                                </div>
                                <input
                                    type="range" min="0" max="1" step="0.1"
                                    value={temperature}
                                    onChange={e => setTemperature(parseFloat(e.target.value))}
                                    className="w-full accent-purple-600"
                                />
                            </div>

                        </div>
                    </div>
                </div>

                {/* RIGHT: SIMULATION PREVIEW */}
                <div className="w-full lg:w-1/2 bg-slate-900 p-8 flex flex-col border-l border-slate-800">

                    {/* Constructed Prompt View */}
                    <div className="bg-black/30 rounded-xl p-4 mb-8 border border-white/10 relative group">
                        <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex justify-between">
                            <span>Compiled Prompt</span>
                            <button
                                onClick={() => navigator.clipboard.writeText(fullPrompt)}
                                className="text-white hover:text-green-400 transition-colors" title="Copy to Clipboard"
                            >
                                <Copy size={14} />
                            </button>
                        </div>
                        <div className="font-mono text-sm text-green-300/80 whitespace-pre-wrap">
                            {fullPrompt || <span className="text-slate-600 italic">// Start building your prompt...</span>}
                        </div>
                    </div>

                    {/* Output Area */}
                    <div className="flex-1 bg-black/50 rounded-2xl border-2 border-slate-700 relative overflow-hidden flex flex-col">
                        <div className="bg-slate-800 px-4 py-2 border-b border-slate-700 flex items-center gap-2">
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                            </div>
                            <div className="text-xs font-mono text-slate-400 ml-2">ssh gorilla-brain-v4</div>
                        </div>

                        <div className="p-6 flex-1 overflow-y-auto font-mono text-sm">
                            {!generatedResponse && !isThinking && (
                                <div className="h-full flex flex-col items-center justify-center text-slate-600 opacity-50">
                                    <Terminal size={48} className="mb-4" />
                                    <p>Awaiting Input...</p>
                                </div>
                            )}

                            {isThinking && (
                                <div className="flex items-center gap-3 text-cyan-400 animate-pulse">
                                    <Bot /> Use the Force, Gorilla...
                                </div>
                            )}

                            {generatedResponse && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-slate-200 whitespace-pre-wrap"
                                >
                                    {generatedResponse}
                                </motion.div>
                            )}
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="mt-8">
                        <button
                            onClick={handleGenerate}
                            disabled={isThinking || (!role && !task)}
                            className={`w-full py-4 rounded-xl font-black text-lg shadow-lg flex items-center justify-center gap-3 transition-all ${isThinking || (!role && !task)
                                ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white transform hover:scale-[1.02] active:scale-95 shadow-purple-900/50'
                                }`}
                        >
                            {isThinking ? <RefreshCw className="animate-spin" /> : <Sparkles />}
                            {isThinking ? 'Generating...' : 'Run Prompt Simulation'}
                        </button>
                    </div>

                </div>
            </div>

            {/* Educational Content Section */}
            <div className="max-w-6xl mx-auto mt-12 space-y-8 px-8 pb-12">

                {/* Theory Section */}
                <div className="bg-white p-8 rounded-3xl shadow-lg border border-purple-100">
                    <h2 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-3">
                        📚 The Art of Prompt Engineering
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg text-slate-700 mb-3">Why Prompts Matter</h3>
                            <p className="text-slate-600 mb-4">
                                AI models are incredibly powerful, but they're only as good as the instructions you give them.
                                <strong> Prompt Engineering</strong> is the skill of crafting clear, effective instructions.
                            </p>
                            <p className="text-slate-600 mb-4">
                                Think of it like ordering food: "Give me something good" gets random results,
                                but "I'd like a medium-rare steak with garlic butter" gets exactly what you want!
                            </p>

                            <div className="bg-purple-50 p-4 rounded-xl border border-purple-200 mt-4">
                                <h4 className="font-bold text-purple-800 mb-2">💡 The Garbage In, Garbage Out Rule</h4>
                                <p className="text-purple-700 text-sm">
                                    Vague prompts = vague answers.<br />
                                    Specific prompts = specific, useful answers.
                                </p>
                            </div>
                        </div>

                        <div className="bg-slate-900 p-6 rounded-xl">
                            <h3 className="font-bold text-lg text-white mb-3">🎯 The CRAFT Framework</h3>

                            <div className="space-y-3">
                                <div className="bg-purple-900/30 p-3 rounded-lg border border-purple-500/30">
                                    <span className="font-bold text-purple-400">C</span>
                                    <span className="text-slate-300 ml-2">Context - Background info the AI needs</span>
                                </div>
                                <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-500/30">
                                    <span className="font-bold text-blue-400">R</span>
                                    <span className="text-slate-300 ml-2">Role - Who should the AI act as?</span>
                                </div>
                                <div className="bg-green-900/30 p-3 rounded-lg border border-green-500/30">
                                    <span className="font-bold text-green-400">A</span>
                                    <span className="text-slate-300 ml-2">Action - What do you want done?</span>
                                </div>
                                <div className="bg-yellow-900/30 p-3 rounded-lg border border-yellow-500/30">
                                    <span className="font-bold text-yellow-400">F</span>
                                    <span className="text-slate-300 ml-2">Format - How should the output look?</span>
                                </div>
                                <div className="bg-red-900/30 p-3 rounded-lg border border-red-500/30">
                                    <span className="font-bold text-red-400">T</span>
                                    <span className="text-slate-300 ml-2">Target - Who is this for?</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Good vs Bad Prompts */}
                <div className="bg-gradient-to-r from-red-50 to-green-50 p-8 rounded-3xl border border-slate-200">
                    <h2 className="text-2xl font-black text-slate-800 mb-6">🆚 Good vs Bad Prompts</h2>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-xl shadow-sm border-2 border-red-200">
                            <h3 className="font-bold text-red-600 mb-3 flex items-center gap-2">
                                ❌ Bad Prompt
                            </h3>
                            <div className="bg-red-50 p-4 rounded-lg text-sm text-red-800 font-mono mb-3">
                                "Write about dogs"
                            </div>
                            <ul className="text-slate-600 text-sm space-y-1">
                                <li>• No context or purpose</li>
                                <li>• No format specified</li>
                                <li>• No target audience</li>
                                <li>• Too vague</li>
                            </ul>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border-2 border-green-200">
                            <h3 className="font-bold text-green-600 mb-3 flex items-center gap-2">
                                ✅ Good Prompt
                            </h3>
                            <div className="bg-green-50 p-4 rounded-lg text-sm text-green-800 font-mono mb-3">
                                "Act as a veterinarian. Write a 200-word blog post about healthy diets for senior dogs. Target audience: first-time dog owners. Include 3 practical tips."
                            </div>
                            <ul className="text-slate-600 text-sm space-y-1">
                                <li>• Clear role (veterinarian)</li>
                                <li>• Specific task (blog post)</li>
                                <li>• Length constraint (200 words)</li>
                                <li>• Target audience defined</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Prompt Patterns */}
                <div className="bg-white p-8 rounded-3xl shadow-lg border border-purple-100">
                    <h2 className="text-xl font-bold text-slate-800 mb-4">🔮 Powerful Prompt Patterns</h2>

                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                            <h3 className="font-bold text-purple-700 mb-2">Few-Shot Learning</h3>
                            <p className="text-purple-600 text-sm">
                                Give 2-3 examples of what you want, then ask for more.
                            </p>
                            <div className="mt-2 text-xs bg-purple-100 p-2 rounded font-mono">
                                "Example 1: X → Y<br />
                                Example 2: A → B<br />
                                Now convert: C → ?"
                            </div>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                            <h3 className="font-bold text-blue-700 mb-2">Chain of Thought</h3>
                            <p className="text-blue-600 text-sm">
                                Ask the AI to think step-by-step for complex problems.
                            </p>
                            <div className="mt-2 text-xs bg-blue-100 p-2 rounded font-mono">
                                "Let's solve this step by step:<br />
                                1. First, identify...<br />
                                2. Then, calculate..."
                            </div>
                        </div>
                        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                            <h3 className="font-bold text-green-700 mb-2">Role Play</h3>
                            <p className="text-green-600 text-sm">
                                Assign a specific expert persona to the AI.
                            </p>
                            <div className="mt-2 text-xs bg-green-100 p-2 rounded font-mono">
                                "You are a senior software engineer at Google with 15 years of experience..."
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pro Tips */}
                <div className="bg-slate-900 p-8 rounded-3xl text-white">
                    <h2 className="text-xl font-bold text-white mb-4">💡 Pro Tips</h2>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white/10 p-4 rounded-xl">
                            <h3 className="font-bold text-purple-300 mb-2">Be Specific About Length</h3>
                            <p className="text-slate-300 text-sm">
                                "In 3 sentences", "100-150 words", "A brief paragraph"
                            </p>
                        </div>
                        <div className="bg-white/10 p-4 rounded-xl">
                            <h3 className="font-bold text-blue-300 mb-2">Define the Format</h3>
                            <p className="text-slate-300 text-sm">
                                "As a bullet list", "In a table", "As JSON", "As markdown"
                            </p>
                        </div>
                        <div className="bg-white/10 p-4 rounded-xl">
                            <h3 className="font-bold text-green-300 mb-2">Set Constraints</h3>
                            <p className="text-slate-300 text-sm">
                                "Don't use jargon", "Assume a 5th grade reading level", "Be concise"
                            </p>
                        </div>
                        <div className="bg-white/10 p-4 rounded-xl">
                            <h3 className="font-bold text-yellow-300 mb-2">Iterate and Refine</h3>
                            <p className="text-slate-300 text-sm">
                                Your first prompt is rarely perfect. Refine based on the output!
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PromptEngineering;
